import { supabase } from "../supabase";

export const LEGAL_VERSION = "2026-09-01.1";
export const LEGAL_EFFECTIVE_DATE = "September 1, 2026";
export const LEGAL_OPERATOR = "Ritual App Labs Inc.";
export const BUSINESS_PHONE_DISPLAY = "226-455-3568";
export const BUSINESS_PHONE_E164 = "+12264553568";
export const ACCOUNT_DELETION_CONFIRMATION = "DELETE MY PRECISE PLATE ACCOUNT";
export const SUPPORT_EMAIL = "support@preciseplate.app";
export const PRIVACY_EMAIL = "privacy@preciseplate.app";
export const BILLING_EMAIL = "billing@preciseplate.app";
export const SECURITY_EMAIL = "security@preciseplate.app";

export type AccountStatus = "active" | "deleted" | "missing";

function parseAccountStatus(data: unknown): AccountStatus {
  if (data !== "active" && data !== "deleted" && data !== "missing") {
    throw new Error("Precise Plate returned an unknown account status.");
  }
  return data;
}

export async function ensurePrecisePlateAccount(): Promise<AccountStatus> {
  const { data, error } = await supabase.rpc("ensure_precise_plate_account");
  if (error) throw error;
  return parseAccountStatus(data);
}

export async function getAccountStatus(): Promise<AccountStatus> {
  const { data, error } = await supabase.rpc("precise_plate_account_status");
  if (error) throw error;
  return parseAccountStatus(data);
}

export async function hasCurrentLegalAcceptance(userId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from("legal_acceptances")
    .select("id")
    .eq("user_id", userId)
    .eq("terms_version", LEGAL_VERSION)
    .eq("privacy_version", LEGAL_VERSION)
    .eq("medical_disclaimer_version", LEGAL_VERSION)
    .limit(1);
  if (error) throw error;
  return Boolean(data?.length);
}

export async function acceptCurrentLegal(source: "signup" | "in_app"): Promise<void> {
  const { data, error } = await supabase.rpc("accept_current_legal", {
    requested_terms_version: LEGAL_VERSION,
    requested_privacy_version: LEGAL_VERSION,
    requested_medical_disclaimer_version: LEGAL_VERSION,
    requested_source: source,
  });
  if (error) throw error;
  if (data !== true) throw new Error("Precise Plate could not confirm that your agreement was saved.");

  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError) throw userError;
  if (!userData.user) throw new Error("Sign in again before accepting the legal documents.");

  for (const delayMs of [0, 150, 500]) {
    if (delayMs) await new Promise((resolve) => window.setTimeout(resolve, delayMs));
    if (await hasCurrentLegalAcceptance(userData.user.id)) return;
  }
  throw new Error("Your agreement was received but could not be confirmed. Please try again.");
}

export async function downloadMyPrecisePlateAccountRecords(): Promise<void> {
  const [{ data, error }, { data: activity, error: activityError }] = await Promise.all([
    supabase.rpc("export_my_precise_plate_data"),
    supabase.rpc("get_my_precise_plate_activity"),
  ]);
  if (error) throw error;
  if (activityError) throw activityError;
  const exportData = typeof data === "object" && data !== null && !Array.isArray(data)
    ? { ...data, app_activity: activity }
    : data;
  const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `precise-plate-account-records-${new Date().toISOString().slice(0, 10)}.json`;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export const downloadMyPrecisePlateData = downloadMyPrecisePlateAccountRecords;

export async function deleteMyPrecisePlateAccount(confirmation: string): Promise<void> {
  const { data, error } = await supabase.rpc("delete_my_precise_plate_account", { confirmation });
  if (error) throw error;
  if (!data || typeof data !== "object" || (data as { status?: unknown }).status !== "deleted") {
    throw new Error("Precise Plate could not confirm that account deletion completed.");
  }
}
